import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import CSS from 'csstype';
import { isEmpty } from 'lodash-es';
import React from 'react';

import { ITargetingPreset } from '~/src/dash/apiV2/targeting/targetingPresets';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { Div, Text, usePanel } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import {
  TargetingPresetParameters,
  TargetingPresetParamKey,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import {
  ITargetingSummaryTableColumn,
  ITargetingSummaryTableRow,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/config';
import { TargetingSummaryTableContext } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingSummaryTable/context';

const skeletonHeight = '23px';

const colNameStyle: CSS.Properties = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  verticalAlign: 'text-bottom',
  left: 0,
  position: 'sticky',
  textAlign: 'left',
  top: 0,
  width: '100%',
  zIndex: 1,
  backgroundColor: publicaColors.veryLightBlue,
};

const colStyle: CSS.Properties = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  verticalAlign: 'text-bottom',
  position: 'relative',
};

interface IProps {
  columns: ITargetingSummaryTableColumn[];
  rows: ITargetingSummaryTableRow[];
  data: ITargetingPreset[];
  lists: Record<string, ISelectOption[]>;
  loadState: LoadingState;
  conflictedKeys: TargetingPresetParamKey[];
}

export const TargetingSummaryTable = ({
  columns,
  rows,
  data,
  lists,
  loadState,
  conflictedKeys = [],
}: IProps) => {
  const paramCfg = TargetingPresetParameters;
  const isUpdating = loadState === LoadingStateLoading;
  const isLoaded = loadState === LoadingStateHasData;
  const isDataEmpty = loadState === LoadingStateNoData;

  const conflicts = new Set(conflictedKeys);

  const { width } = usePanel();

  return (
    <TargetingSummaryTableContext.Provider value={{ lists }}>
      <Div style={{ width: width ? `${width - 64}px` : 'auto', minWidth: '720px' }}>
        <TableContainer>
          <Table
            aria-labelledby='targeting-summary-table'
            size='small'
            className={`cypress-targetingsummary-table`}
          >
            <TableHead>
              <TableRow>
                {columns.map((column, i) => {
                  const { key, label } = column;
                  return (
                    <TableCell key={key} style={i === 0 ? colNameStyle : colStyle}>
                      <Text small bold>
                        {label}
                      </Text>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {(isDataEmpty || isEmpty(rows)) && (
                <TableRow style={{ height: isUpdating ? 'auto' : '96px' }}>
                  <TableCell
                    colSpan={columns.length}
                    style={{
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {isUpdating ? <Skeleton variant='text' animation='wave' /> : 'No Data Found.'}
                  </TableCell>
                </TableRow>
              )}
              {(isLoaded || isUpdating) &&
                !isEmpty(rows) &&
                rows.map((row) => {
                  const { SummaryComponent, summaryProps } = paramCfg[row.key];
                  return (
                    <TableRow
                      style={{
                        backgroundColor: conflicts.has(row.key)
                          ? publicaColors.veryLightRed
                          : 'inherit',
                      }}
                    >
                      {/* NAME */}
                      <TableCell
                        component='th'
                        id={row.key}
                        scope='row'
                        style={{
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          verticalAlign: 'text-top',
                          minWidth: '192px',
                          maxWidth: '320px',
                          position: 'sticky',
                          top: 1,
                          left: 0,
                          borderBottom: `1px solid ${publicaColors.lightGray}`,
                          zIndex: 1,
                          backgroundColor: conflicts.has(row.key)
                            ? publicaColors.veryLightRed
                            : publicaColors.veryLightBlue,
                        }}
                      >
                        {isUpdating ? (
                          <Skeleton variant='text' animation='wave' height={skeletonHeight} />
                        ) : (
                          <Text small bold>
                            {row.label}
                          </Text>
                        )}
                      </TableCell>
                      {data.map((item) => {
                        return (
                          <TableCell
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              verticalAlign: 'text-top',
                              minWidth: '160px',
                              maxWidth: '320px',
                            }}
                          >
                            {isUpdating ? (
                              <Skeleton variant='text' animation='wave' height={skeletonHeight} />
                            ) : (
                              (SummaryComponent && (
                                <SummaryComponent
                                  {...summaryProps}
                                  preset={item}
                                  paramKey={row.key}
                                />
                              )) ||
                              'Work In Progress'
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Div>
    </TargetingSummaryTableContext.Provider>
  );
};
